// Streaming SSE client for the Chronicles assistant.

import { getToken } from "@/lib/api";
import { ignore } from "@/lib/log";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * streamAssistant({ payload, onDelta, onDone, onError, signal })
 * payload = { session_id, mode, message, history, events, today }
 */
export async function streamAssistant({ payload, onDelta, onDone, onError, signal }) {
  let response;
  try {
    const token = getToken();
    response = await fetch(`${API}/assistant/chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      signal,
    });
  } catch (err) {
    onError?.(err);
    return;
  }

  if (!response.ok || !response.body) {
    let detail = `HTTP ${response.status}`;
    try {
      const json = await response.json();
      detail = json.detail || detail;
    } catch { ignore(); }
    onError?.(new Error(detail));
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop() || "";
      for (const evt of events) {
        const line = evt.trim();
        if (!line.startsWith("data:")) continue;
        const data = line.slice(5).trim();
        if (!data) continue;
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === "delta") {
            onDelta?.(parsed.content || "");
          } else if (parsed.type === "done") {
            onDone?.();
            return;
          } else if (parsed.type === "error") {
            onError?.(new Error(parsed.message || "Assistant error"));
            return;
          }
        } catch {
          ignore(); // non-JSON keep-alive / partial SSE frame
        }
      }
    }
    onDone?.();
  } catch (err) {
    if (err.name !== "AbortError") onError?.(err);
  }
}

/**
 * transcribeAudio(blob, lang) — send a recorded audio blob to the backend
 * Whisper transcription endpoint and return the recognized text.
 */
export async function transcribeAudio(blob, lang) {
  const token = getToken();
  const form = new FormData();
  const ext = blob.type && blob.type.includes("webm") ? "webm" : blob.type && blob.type.includes("mp4") ? "mp4" : "wav";
  form.append("audio", blob, `recording.${ext}`);
  if (lang) form.append("lang", lang);
  const res = await fetch(`${API}/assistant/transcribe`, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      detail = j.detail || detail;
    } catch {
      ignore();
    }
    throw new Error(detail);
  }
  const data = await res.json();
  return (data.text || "").trim();
}
